import React, { useState } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, Modal, FlatList } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius } from '../utils/theme';
import type { ColorTheme } from '../utils/theme';
import { useModalBottomPadding } from '../utils/useModalBottomPadding';

interface PickerOption {
  readonly label: string;
  readonly value: string;
}

/** Picker - cross-platform select dropdown; native HTML select on web, modal list on native. */
interface PickerProps {
  readonly options: readonly PickerOption[];
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly label?: string;
}

export default function Picker({ options, value, onValueChange, label }: PickerProps) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const modalBottomPadding = useModalBottomPadding();
  const [modalVisible, setModalVisible] = useState(false);

  const selectedLabel = options.find(o => o.value === value)?.label || 'Select...';

  if (Platform.OS === 'web') {
    return (
      <View>
        {label && <Text style={styles.label}>{label}</Text>}
        <View style={styles.webSelectWrapper}>
          {/* @ts-ignore - web-only DOM element not typed in RN */}
          <select
            value={value}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onValueChange(e.target.value)}
            style={{
              width: '100%',
              padding: 12,
              fontSize: 15,
              borderRadius: 8,
              border: `1.5px solid ${colors.border}`,
              backgroundColor: colors.surface,
              color: colors.text,
              appearance: 'none' as const,
              cursor: 'pointer',
              backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23666\' d=\'M6 8L1 3h10z\'/%3E%3C/svg%3E")',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 12px center',
            }}
          >
            {options.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </View>
      </View>
    );
  }

  // Native: modal-based dropdown
  return (
    <View>
      {label && <Text style={styles.label}>{label}</Text>}
      <TouchableOpacity style={styles.trigger} onPress={() => setModalVisible(true)} activeOpacity={0.7}>
        <Text style={styles.triggerText}>{selectedLabel}</Text>
        <Ionicons name="chevron-down" size={18} color={colors.textLight} />
      </TouchableOpacity>
      <Modal visible={modalVisible} transparent animationType="fade" onRequestClose={() => setModalVisible(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setModalVisible(false)}>
          <View style={[styles.modalContent, { marginBottom: modalBottomPadding }]}>
            <Text style={styles.modalTitle}>{label || 'Select'}</Text>
            <FlatList
              data={options as PickerOption[]}
              keyExtractor={item => item.value}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[styles.modalOption, item.value === value && styles.modalOptionSelected]}
                  onPress={() => { onValueChange(item.value); setModalVisible(false); }}
                >
                  <Text style={[styles.modalOptionText, item.value === value && styles.modalOptionTextSelected]}>
                    {item.label}
                  </Text>
                  {item.value === value && <Ionicons name="checkmark" size={18} color={colors.primary} />}
                </TouchableOpacity>
              )}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  label: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    color: c.textSecondary,
    marginBottom: spacing.sm,
  },
  webSelectWrapper: {
    marginTop: spacing.xs,
  },
  trigger: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.md,
    borderRadius: borderRadius.md,
    borderWidth: 1.5,
    borderColor: c.border,
    backgroundColor: c.surface,
  },
  triggerText: {
    fontSize: fontSize.sm,
    color: c.text,
    flex: 1,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: c.surface,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    width: '85%',
    maxWidth: 360,
    maxHeight: '70%',
  },
  modalTitle: {
    fontSize: fontSize.md,
    fontWeight: fontWeight.semibold,
    color: c.text,
    marginBottom: spacing.md,
    textAlign: 'center',
  },
  modalOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm + 2,
    paddingHorizontal: spacing.sm,
    borderRadius: borderRadius.md,
    marginBottom: 2,
  },
  modalOptionSelected: {
    backgroundColor: c.primaryBg,
  },
  modalOptionText: {
    fontSize: fontSize.sm,
    color: c.text,
  },
  modalOptionTextSelected: {
    color: c.primary,
    fontWeight: fontWeight.semibold,
  },
});

const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
